export function PrivacyPage() {
  return (
    <article className="prose-like mx-auto max-w-2xl py-8">
      <h1 className="text-3xl font-semibold tracking-tight">Privacy Policy</h1>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Last updated: April 2026
      </p>

      <p className="mt-8 text-sm text-muted-foreground">
        Short version: we collect what we need to run the app, we don't sell your data to anyone,
        and we don't do ad tracking. The long version is below.
      </p>

      <H>What we collect</H>
      <P>
        When you create an account we store your email, a hashed password, your handle, and
        whatever profile info you put in yourself (display name, bio, avatar, payment link).
      </P>
      <P>
        When you use the product we store the stuff you created: services, jobs, messages,
        checkpoints, revisions, payments, testimonials, and any files you uploaded (references,
        WIPs, deliverables).
      </P>
      <P>
        When your browser hits our server we write a short access log (IP, URL, status, timing).
        These logs roll off quickly. They exist so we can debug and block abuse.
      </P>

      <H>What we don't collect</H>
      <P>
        No third-party analytics. No Facebook pixel. No Google Tag Manager. No session replay.
        No ad network nonsense.
      </P>

      <H>Cookies</H>
      <P>
        One cookie: <code>fx_sess</code>. It holds a random token that maps to your session on
        our side. It's <code>HttpOnly</code>, <code>Secure</code>, and
        <code> SameSite=Lax</code>. When you log out it's wiped. That's the whole cookie story.
      </P>

      <H>Payments</H>
      <P>
        Payments are handled off-platform for now — creators post a payment link (Stripe,
        Ko-fi, whatever they use) and the money moves through that processor. We don't see card
        numbers. We record that a payment was marked paid and what it was for.
      </P>

      <H>File uploads</H>
      <P>
        Uploads go to a private R2 bucket we control. References and WIPs are visible to you and
        the other party on the job. Deliverables unlock for the client after the final payment is
        marked paid. Public assets (avatar, cover image) are served publicly by design.
      </P>

      <H>Who sees your data</H>
      <P>
        You, and the other party on a job you're part of. That's it. We don't share with
        advertisers. We don't train AI on your stuff. If the law hands us a valid subpoena we'll
        comply, and we'll tell you unless a court tells us we can't.
      </P>

      <H>Deletion</H>
      <P>
        You can delete your account from Settings. Email{" "}
        <a className="underline" href="mailto:privacy@fanfix.me">
          privacy@fanfix.me
        </a>{" "}
        if something's stuck. Active job records may stick around until both sides are done,
        because deleting one half of a contract mid-flight is a bad idea.
      </P>

      <H>Changes</H>
      <P>
        If this policy changes materially we'll update the date at the top and notify logged-in
        users on next login. Minor wording cleanups happen without fanfare.
      </P>

      <H>Contact</H>
      <P>
        <a className="underline" href="mailto:privacy@fanfix.me">privacy@fanfix.me</a>
      </P>
    </article>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-10 mb-2 text-lg font-semibold tracking-tight">{children}</h2>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;
}
